import { makeAutoObservable, action, runInAction, observable } from 'mobx';

export class SettingsStore {
    // Default settings
    settings = {
        recentFiles: [],
        maxRecentFiles: 10,
        accessibleDirectories: [], // Store directory paths we've accessed
        theme: 'light',
    };
    
    isLoaded = false;

    constructor(rootStore) {
        this.rootStore = rootStore;
        makeAutoObservable(this, {
            rootStore: false,
            settings: observable,
            isLoaded: observable
        });
        
        // Load settings from localStorage on initialization
        this.loadSettings();
    }

    loadSettings = action(async () => {
        try {
            const storedSettings = localStorage.getItem('appSettings');
            if (storedSettings) {
                const parsedSettings = JSON.parse(storedSettings);
                runInAction(() => {
                    this.settings = { ...this.settings, ...parsedSettings };
                });
            }
        } catch (error) {
            console.error('Error loading settings from localStorage:', error);
        } finally {
            runInAction(() => {
                this.isLoaded = true;
            });
        }
    });

    saveSettings = action(() => {
        try {
            localStorage.setItem('appSettings', JSON.stringify(this.settings));
        } catch (error) {
            console.error('Error saving settings to localStorage:', error);
        }
    });

    updateSetting = action((key, value) => {
        if (this.settings.hasOwnProperty(key)) {
            this.settings[key] = value;
            this.saveSettings();
        }
    });

    // Add a directory to accessible directories list
    addAccessibleDirectory = action((dirPath) => {
        if (!dirPath) return;
        
        // Don't add duplicates
        if (!this.settings.accessibleDirectories.includes(dirPath)) {
            this.settings.accessibleDirectories = [
                dirPath,
                ...this.settings.accessibleDirectories
            ];
            this.saveSettings();
        }
    });

    // Specific method for recent files - now with directory info
    addRecentFile = action((fileInfo) => {
        if (!fileInfo || !fileInfo.name) return;
        
        // Create a serializable version of the file info
        const serializableFileInfo = {
            name: fileInfo.name,
            directoryPath: fileInfo.directoryPath || null, // Store directory path
            relativePath: fileInfo.relativePath || fileInfo.name, // Path relative to directory
            size: fileInfo.size,
            lastModified: fileInfo.lastModified,
            lastOpened: fileInfo.lastOpened || new Date().toISOString()
        };
        
        // Create a unique identifier for the file
        const fileId = serializableFileInfo.directoryPath 
            ? `${serializableFileInfo.directoryPath}/${serializableFileInfo.relativePath}`
            : serializableFileInfo.name;
            
        // Remove the file if it already exists in the list
        const updatedRecentFiles = this.settings.recentFiles.filter(
            file => {
                const existingId = file.directoryPath 
                    ? `${file.directoryPath}/${file.relativePath}`
                    : file.name;
                return existingId !== fileId;
            }
        );
        
        // Add the file to the beginning of the list
        updatedRecentFiles.unshift(serializableFileInfo);
        
        // Limit the number of recent files
        if (updatedRecentFiles.length > this.settings.maxRecentFiles) {
            updatedRecentFiles.pop();
        }
        
        this.settings.recentFiles = updatedRecentFiles;
        this.saveSettings();
    });

    updateRecentFileTimestamp = action((fileInfo) => {
        if (!fileInfo) return;
        
        // Create a unique identifier for the file
        const fileId = fileInfo.directoryPath 
            ? `${fileInfo.directoryPath}/${fileInfo.relativePath}`
            : fileInfo.name;
            
        const fileIndex = this.settings.recentFiles.findIndex(file => {
            const existingId = file.directoryPath 
                ? `${file.directoryPath}/${file.relativePath}`
                : file.name;
            return existingId === fileId;
        });
        
        if (fileIndex >= 0) {
            const updatedFiles = [...this.settings.recentFiles];
            updatedFiles[fileIndex] = {
                ...updatedFiles[fileIndex],
                lastOpened: new Date().toISOString()
            };
            
            // Move the file to the top of the list
            const updatedFile = updatedFiles.splice(fileIndex, 1)[0];
            updatedFiles.unshift(updatedFile);
            
            this.settings.recentFiles = updatedFiles;
            this.saveSettings();
        }
    });

    clearRecentFiles = action(() => {
        this.settings.recentFiles = [];
        this.saveSettings();
    });
} 